/* Dr Cozy delivery calculator: Nominatim geocoding + haversine distance + Leaflet map (loaded on demand). */
(function () {
  if (!window.customElements || customElements.get('dc-delivery-calculator')) return;

  var LEAFLET_CSS = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css';
  var LEAFLET_JS = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js';
  var NOMINATIM = 'https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=us&q=';
  var MIN_GAP_MS = 1100; /* Nominatim usage policy: max 1 request per second */
  var lastRequest = 0;
  var leafletPromise = null;

  function loadLeaflet() {
    if (window.L && window.L.map) return Promise.resolve(window.L);
    if (leafletPromise) return leafletPromise;
    leafletPromise = new Promise(function (resolve, reject) {
      var css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = LEAFLET_CSS;
      document.head.appendChild(css);
      var s = document.createElement('script');
      s.src = LEAFLET_JS;
      s.async = true;
      s.onload = function () { if (window.L && window.L.map) resolve(window.L); else reject(new Error('Leaflet unavailable')); };
      s.onerror = function () { leafletPromise = null; reject(new Error('Leaflet failed to load')); };
      document.head.appendChild(s);
    });
    return leafletPromise;
  }

  function haversineMiles(a, b) {
    var R = 3958.7613;
    var rad = Math.PI / 180;
    var dLat = (b.lat - a.lat) * rad;
    var dLng = (b.lng - a.lng) * rad;
    var h = Math.pow(Math.sin(dLat / 2), 2) + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.pow(Math.sin(dLng / 2), 2);
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
  }

  function wait(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }

  function shortLabel(label) { return String(label).split(',').slice(0, 4).join(',').trim(); }

  class DcDeliveryCalculator extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.origin = { lat: parseFloat(this.dataset.lat), lng: parseFloat(this.dataset.lng) };
      var tiers = [];
      try { tiers = JSON.parse(this.querySelector('[data-dc-tiers]').textContent) || []; } catch (e) { tiers = []; }
      this.tiers = tiers.filter(function (t) { return t && isFinite(t.max); }).sort(function (a, b) { return a.max - b.max; });
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[name="address"]');
      this.button = this.querySelector('[data-dc-submit]');
      this.status = this.querySelector('[data-dc-status]');
      this.result = this.querySelector('[data-dc-result]');
      this.feeEl = this.querySelector('[data-dc-fee]');
      this.metaEl = this.querySelector('[data-dc-meta]');
      this.mapEl = this.querySelector('[data-dc-map]');
      this.busy = false;
      var self = this;
      this.form.addEventListener('submit', function (e) { e.preventDefault(); self.lookup(); });
    }

    setStatus(msg, isError) {
      this.status.textContent = msg || '';
      this.status.classList.toggle('is-error', !!isError);
    }

    lookup() {
      if (this.busy) return; /* one lookup per click */
      var self = this;
      var q = (this.input.value || '').trim();
      if (q.length < 5) {
        this.setStatus('Please enter a street address, city and ZIP code.', true);
        this.input.focus();
        return;
      }
      this.busy = true;
      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      this.result.hidden = true;
      this.setStatus('Looking up your address…');

      var gap = Date.now() - lastRequest;
      var controller = 'AbortController' in window ? new AbortController() : null;
      var timer = null;

      wait(gap < MIN_GAP_MS ? MIN_GAP_MS - gap : 0)
        .then(function () {
          lastRequest = Date.now();
          if (controller) timer = setTimeout(function () { controller.abort(); }, 12000);
          return fetch(NOMINATIM + encodeURIComponent(q), {
            headers: { 'Accept': 'application/json', 'Accept-Language': 'en' },
            signal: controller ? controller.signal : undefined
          });
        })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        })
        .then(function (data) {
          if (!Array.isArray(data) || !data.length) {
            self.setStatus("We couldn't find that address. Try including the street number, city and ZIP code.", true);
            return;
          }
          var dest = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
          if (!isFinite(dest.lat) || !isFinite(dest.lng)) throw new Error('Bad coordinates');
          self.showResult(haversineMiles(self.origin, dest), data[0].display_name || q);
          self.setStatus('');
          self.drawMap(dest);
        })
        .catch(function () {
          self.setStatus("We couldn't reach the address lookup service. Please try again in a moment, or call us for a delivery quote.", true);
        })
        .then(function () {
          if (timer) clearTimeout(timer);
          self.busy = false;
          self.button.disabled = false;
          self.button.removeAttribute('aria-busy');
        });
    }

    showResult(miles, label) {
      var tier = null;
      for (var i = 0; i < this.tiers.length; i++) {
        if (miles <= this.tiers[i].max) { tier = this.tiers[i]; break; }
      }
      this.feeEl.textContent = '';
      if (tier) {
        this.feeEl.textContent = tier.fee;
        this.result.classList.remove('is-quote');
      } else {
        var tpl = this.querySelector('template[data-dc-over]');
        if (tpl) this.feeEl.appendChild(tpl.content.cloneNode(true));
        this.result.classList.add('is-quote');
      }
      var rounded = miles < 10 ? miles.toFixed(1) : String(Math.round(miles));
      this.metaEl.textContent = 'About ' + rounded + ' miles (straight line) from our showroom to ' + shortLabel(label) + '.';
      this.result.hidden = false;
    }

    drawMap(dest) {
      var self = this;
      loadLeaflet().then(function (L) {
        self.mapEl.hidden = false;
        if (!self.map) {
          self.map = L.map(self.mapEl, { scrollWheelZoom: false });
          L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          }).addTo(self.map);
          self.layer = L.layerGroup().addTo(self.map);
        }
        self.layer.clearLayers();
        var o = [self.origin.lat, self.origin.lng];
        var d = [dest.lat, dest.lng];
        var accent = getComputedStyle(self).getPropertyValue('--dc-accent').trim() || '#1E4D3F';
        L.polyline([o, d], { color: accent, weight: 3, dashArray: '6 8' }).addTo(self.layer);
        L.circleMarker(o, { radius: 9, color: '#FFFFFF', weight: 3, fillColor: accent, fillOpacity: 1 }).bindTooltip('Our showroom').addTo(self.layer);
        L.circleMarker(d, { radius: 9, color: '#FFFFFF', weight: 3, fillColor: '#9A3412', fillOpacity: 1 }).bindTooltip('Your address').addTo(self.layer);
        self.map.invalidateSize();
        self.map.fitBounds(L.latLngBounds([o, d]), { padding: [36, 36], maxZoom: 13 });
      }).catch(function () { self.mapEl.hidden = true; });
    }
  }

  customElements.define('dc-delivery-calculator', DcDeliveryCalculator);
})();